import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Autocomplete, Box, TextField, MenuItem, FormControl, Select, InputLabel } from "@mui/material";
import SearchInput from "../../Components/SearchInput";
import Cookies from "universal-cookie";
import municipalityAPI from "../../Network/Municipality";
import contractorAPI from "../../Network/Contractor";
import accountAPI from "../../Network/Account";

const cookies = new Cookies();

export default function MizanUnitsSearch({
    filters,
    setFilters,
    lookups,
    loadingLookups
}) {
    const [t] = useTranslation("common");

    // State for autocomplete fields
    const [municipalities, setMunicipalities] = useState([]);
    const [contractors, setContractors] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [loadingMunicipalities, setLoadingMunicipalities] = useState(false);
    const [loadingContractors, setLoadingContractors] = useState(false);
    const [loadingAccounts, setLoadingAccounts] = useState(false);

    const headerObject = {
        username: cookies.get("user_name") || null,
        user_id: cookies.get("user_id") || null,
        account_name: cookies.get("account_name") || null,
        account_id: cookies.get("account_id") || null,
        authHash: cookies.get("authHash") || null,
        sid: cookies.get("sid") || null,
        baseurl: cookies.get("baseUrl") || null,
        id: cookies.get("id") || null,
    };

    // Load initial data (top 30 records)
    useEffect(() => {
        loadInitialMunicipalities();
        loadInitialContractors();
        loadInitialAccounts();
    }, []);

    const loadInitialMunicipalities = async () => {
        setLoadingMunicipalities(true);
        try {
            const result = await municipalityAPI.getMunicipalities({
                params: { ...headerObject, page_size: 30, page: 1 }
            });
            if (result.status && result?.data?.data?.data) {
                setMunicipalities(result.data.data.data);
            }
        } catch (error) {
            console.error('Error loading municipalities:', error);
        }
        setLoadingMunicipalities(false);
    };

    const loadInitialContractors = async () => {
        setLoadingContractors(true);
        try {
            const result = await contractorAPI.getContractors({
                params: { ...headerObject, page_size: 30, page: 1 }
            });
            if (result.status && result?.data?.data?.data) {
                setContractors(result.data.data.data);
            }
        } catch (error) {
            console.error('Error loading contractors:', error);
        }
        setLoadingContractors(false);
    };

    const loadInitialAccounts = async () => {
        setLoadingAccounts(true);
        try {
            const result = await accountAPI.getAccounts({
                params: { ...headerObject, page_size: 30, page: 1 }
            });
            if (result.status && result?.data?.data) {
                setAccounts(result.data.data);
            }
        } catch (error) {
            console.error('Error loading accounts:', error);
        }
        setLoadingAccounts(false);
    };

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                width: "100% !important",
            }}
        >
            {/* Name */}
            <SearchInput
                value={filters?.name}
                setValue={(value) => {
                    setFilters({
                        ...filters,
                        name: value
                    })
                }}
                title={t('MIZAN_UNITS.unit_name') || 'اسم الوحدة'}
            />

            {/* Tag Code */}
            <SearchInput
                value={filters?.tag_code}
                setValue={(value) => {
                    setFilters({
                        ...filters,
                        tag_code: value
                    })
                }}
                title={t('MIZAN_UNITS.tag_code') || 'رمز الملصق'}
            />

            {/* Barcode */}
            <SearchInput
                value={filters?.barcode}
                setValue={(value) => {
                    setFilters({
                        ...filters,
                        barcode: value
                    })
                }}
                title={t('MIZAN_UNITS.barcode') || 'الباركود'}
            />

            {/* Municipality */}
            <Autocomplete
                sx={{
                    ml: 1.5, mt: 1, mb: 1, width: '95%',
                    '& .MuiFormLabel-root,& .MuiInputBase-input': {
                        fontFamily: 'Cairo-Medium'
                    }
                }}
                value={filters?.municipality_selected || null}
                onChange={(e, v) => {
                    setFilters({
                        ...filters,
                        municipality_id: v?.id || null,
                        municipality_selected: v
                    })
                }}
                options={municipalities}
                loading={loadingMunicipalities}
                loadingText={t('GLOBAL.searching')}
                getOptionLabel={(option) => option && option?.name || ""}
                renderInput={(params) => (
                    <TextField {...params}
                        placeholder={t('MIZAN_UNITS.municipality') || 'البلدية'}
                        variant="standard" sx={{ fontFamily: 'Cairo-Medium' }} />
                )}
            />

            {/* Contractor */}
            <Autocomplete
                sx={{
                    ml: 1.5, mt: 1, mb: 1, width: '95%',
                    '& .MuiFormLabel-root,& .MuiInputBase-input': {
                        fontFamily: 'Cairo-Medium'
                    }
                }}
                value={filters?.contractor_selected || null}
                onChange={(e, v) => {
                    setFilters({
                        ...filters,
                        contractor_id: v?.id || null,
                        contractor_selected: v
                    })
                }}
                options={contractors}
                loading={loadingContractors}
                loadingText={t('GLOBAL.searching')}
                getOptionLabel={(option) => option && option?.name || ""}
                renderInput={(params) => (
                    <TextField {...params}
                        placeholder={t('MIZAN_UNITS.contractor') || 'المتعهد'}
                        variant="standard" sx={{ fontFamily: 'Cairo-Medium' }} />
                )}
            />

            {/* Account */}
            <Autocomplete
                sx={{
                    ml: 1.5, mt: 1, mb: 1, width: '95%',
                    '& .MuiFormLabel-root,& .MuiInputBase-input': {
                        fontFamily: 'Cairo-Medium'
                    }
                }}
                value={filters?.account_selected || null}
                onChange={(e, v) => {
                    setFilters({
                        ...filters,
                        mizan_account_id: v?.account_id || null,
                        account_selected: v
                    })
                }}
                options={accounts}
                loading={loadingAccounts}
                loadingText={t('GLOBAL.searching')}
                getOptionLabel={(option) => option && option?.account_name || ""}
                renderInput={(params) => (
                    <TextField {...params}
                        placeholder={t('MIZAN_UNITS.account_name') || 'اسم الحساب'}
                        variant="standard" sx={{ fontFamily: 'Cairo-Medium' }} />
                )}
            />

            {/* Is Governmental */}
            <FormControl variant="standard" sx={{ ml: 1.5, mt: 1, mb: 1, width: '95%' }}>
                <InputLabel sx={{ fontFamily: 'Cairo-Medium' }}>
                    {t('MIZAN_UNITS.is_governmental') || 'نوع الجهد'}
                </InputLabel>
                <Select
                    value={filters?.is_governmental}
                    onChange={(e) => {
                        setFilters({
                            ...filters,
                            is_governmental: e.target.value
                        })
                    }}
                    sx={{ fontFamily: 'Cairo-Medium' }}
                >
                    <MenuItem value="" sx={{ fontFamily: 'Cairo-Medium' }}>الكل</MenuItem>
                    <MenuItem value={1} sx={{ fontFamily: 'Cairo-Medium' }}>{t('MIZAN_UNITS.governmental_owned') || 'جهد ذاتي'}</MenuItem>
                    <MenuItem value={0} sx={{ fontFamily: 'Cairo-Medium' }}>{t('MIZAN_UNITS.governmental_rented') || 'جهد مؤجر'}</MenuItem>
                </Select>
            </FormControl>

            {/* Unit Type */}
            <Autocomplete
                sx={{
                    ml: 1.5, mt: 1, mb: 1, width: '95%',
                    '& .MuiFormLabel-root,& .MuiInputBase-input': {
                        fontFamily: 'Cairo-Medium'
                    }
                }}
                value={filters?.unit_type_selected || null}
                onChange={(e, v) => {
                    setFilters({
                        ...filters,
                        unit_type_id: v?.id || null,
                        unit_type_selected: v
                    })
                }}
                options={lookups?.unit_types || []}
                loading={loadingLookups}
                loadingText={t('GLOBAL.searching')}
                getOptionLabel={(option) => option && option?.name || ""}
                renderInput={(params) => (
                    <TextField {...params}
                        placeholder={t('MIZAN_UNITS.unit_type') || 'نوع الوحدة'}
                        variant="standard" sx={{ fontFamily: 'Cairo-Medium' }} />
                )}
            />

            {/* Lifecycle Mode */}
            <FormControl variant="standard" sx={{ ml: 1.5, mt: 1, mb: 1, width: '95%' }}>
                <InputLabel sx={{ fontFamily: 'Cairo-Medium' }}>
                    {t('MIZAN_UNITS.lifecycle_mode') || 'نوع العمل'}
                </InputLabel>
                <Select
                    value={filters?.lifecycle_mode}
                    onChange={(e) => {
                        setFilters({
                            ...filters,
                            lifecycle_mode: e.target.value
                        })
                    }}
                    sx={{ fontFamily: 'Cairo-Medium' }}
                >
                    <MenuItem value="" sx={{ fontFamily: 'Cairo-Medium' }}>الكل</MenuItem>
                    <MenuItem value="reverse_complete" sx={{ fontFamily: 'Cairo-Medium' }}>{t('MIZAN_UNITS.lifecycle_external') || 'جهد خارجي'}</MenuItem>
                    <MenuItem value="normal" sx={{ fontFamily: 'Cairo-Medium' }}>{t('MIZAN_UNITS.lifecycle_internal') || 'جهد داخلي'}</MenuItem>
                </Select>
            </FormControl>

            {/* Capacity */}
            <SearchInput
                value={filters?.capacity}
                setValue={(value) => {
                    setFilters({
                        ...filters,
                        capacity: value
                    })
                }}
                title={t('MIZAN_UNITS.capacity') || 'السعة'}
            />

            {/* Mizan */}
            <Autocomplete
                sx={{
                    ml: 1.5, mt: 1, mb: 1, width: '95%',
                    '& .MuiFormLabel-root,& .MuiInputBase-input': {
                        fontFamily: 'Cairo-Medium'
                    }
                }}
                value={filters?.mizan_selected || null}
                onChange={(e, v) => {
                    setFilters({
                        ...filters,
                        mizan_id: v?.id || null,
                        mizan_selected: v,
                        // Clear belonging when mizan changes
                        belonging_id: null,
                        belonging_selected: null
                    })
                }}
                options={lookups?.mizans || []}
                loading={loadingLookups}
                loadingText={t('GLOBAL.searching')}
                getOptionLabel={(option) => option && option?.name || ""}
                renderInput={(params) => (
                    <TextField {...params}
                        placeholder={t('MIZAN_UNITS.mizan') || 'الميزان'}
                        variant="standard" sx={{ fontFamily: 'Cairo-Medium' }} />
                )}
            />

            {/* Belonging - filtered by selected mizan */}
            <Autocomplete
                sx={{
                    ml: 1.5, mt: 1, mb: 1, width: '95%',
                    '& .MuiFormLabel-root,& .MuiInputBase-input': {
                        fontFamily: 'Cairo-Medium'
                    }
                }}
                value={filters?.belonging_selected || null}
                onChange={(e, v) => {
                    setFilters({
                        ...filters,
                        belonging_id: v?.id || null,
                        belonging_selected: v
                    })
                }}
                options={
                    filters?.mizan_id
                        ? (lookups?.belongings || []).filter(b => b.mizan_id === filters.mizan_id)
                        : (lookups?.belongings || [])
                }
                disabled={!filters?.mizan_id}
                loading={loadingLookups}
                loadingText={t('GLOBAL.searching')}
                getOptionLabel={(option) => option && option?.name || ""}
                renderInput={(params) => (
                    <TextField {...params}
                        placeholder={t('MIZAN_UNITS.belonging') || 'الانتماء'}
                        variant="standard" sx={{ fontFamily: 'Cairo-Medium' }} />
                )}
            />

            {/* Lifecycle */}
            <Autocomplete
                sx={{
                    ml: 1.5, mt: 1, mb: 1, width: '95%',
                    '& .MuiFormLabel-root,& .MuiInputBase-input': {
                        fontFamily: 'Cairo-Medium'
                    }
                }}
                value={filters?.lifecycle_selected || null}
                onChange={(e, v) => {
                    setFilters({
                        ...filters,
                        lifecycle_id: v?.id || null,
                        lifecycle_selected: v
                    })
                }}
                options={lookups?.lifecycles || []}
                loading={loadingLookups}
                loadingText={t('GLOBAL.searching')}
                getOptionLabel={(option) => option && option?.name || ""}
                renderInput={(params) => (
                    <TextField {...params}
                        placeholder={t('MIZAN_UNITS.lifecycle') || 'دورة الحياة'}
                        variant="standard" sx={{ fontFamily: 'Cairo-Medium' }} />
                )}
            />
        </Box>
    );
}
